import React, { useState, useEffect } from 'react';
import Navbar from '../components/common/Navbar';
import Alert from '../components/common/Alert';
import Modal from '../components/common/Modal';
import { dialoguesAPI, adminAPI } from '../services/api';
import { useAlertContext } from '../context/AlertContext';
import {
	formatDate,
	getDialogueStatus,
	utcToLocalDatetimeInput,
	localToUTC,
} from '../utils/dateUtils';

const DialoguesPage = () => {
	const [dialogues, setDialogues] = useState([]);
	const [filter, setFilter] = useState('0'); // 0 = All, 1 = Not Started, 2 = Active, 3 = Expired
	const [modalOpen, setModalOpen] = useState(false);
	const [groupModalOpen, setGroupModalOpen] = useState(false);
	const [selectedDialogue, setSelectedDialogue] = useState(null);
	const [groups, setGroups] = useState([]);
	const [moderationProfiles, setModerationProfiles] = useState([]);
	const { showAlert } = useAlertContext();
	const baseUrl = process.env.REACT_APP_BASE_URL || '/';

	// Form state
	const [dialogueId, setDialogueId] = useState('');
	const [moderationProfile, setModerationProfile] = useState('');
	const [topic, setTopic] = useState('');
	const [description, setDescription] = useState('');
	const [startTime, setStartTime] = useState('');
	const [endTime, setEndTime] = useState('');
	const [numGroups, setNumGroups] = useState(1);
	const [numStudents, setNumStudents] = useState(1);
	const [outputType, setOutputType] = useState('both');
	const [dialogueType, setDialogueType] = useState('both');
	const [documents, setDocuments] = useState(null);
	const [associatedDocuments, setAssociatedDocuments] = useState([]);
	const [formDisabled, setFormDisabled] = useState(false);

	useEffect(() => {
		fetchDialogues();
		fetchModerationProfiles();
	}, []);

	const fetchDialogues = async () => {
		try {
			const response = await dialoguesAPI.getDialogues();
			if (!response.error) {
				setDialogues(response);
			} else {
				showAlert(response.error, 'danger');
			}
		} catch (error) {
			console.error('Error fetching dialogues:', error);
			showAlert('Failed to load dialogues', 'danger');
		}
	};

	const fetchModerationProfiles = async () => {
		try {
			const response = await adminAPI.getModerationProfiles();
			if (!response.error) {
				setModerationProfiles(response);
			} else {
				showAlert(response.error, 'danger');
			}
		} catch (error) {
			console.error('Error fetching moderation profiles:', error);
			showAlert('Failed to load moderation profiles', 'danger');
		}
	};

	const handleFilterChange = (e) => {
		setFilter(e.target.value);
	};

	const createDialogue = () => {
		setSelectedDialogue(null);
		resetForm();
		setFormDisabled(false);
		setModalOpen(true);
	};

	const editDialogue = async (id) => {
		try {
			const dialogue = await dialoguesAPI.getDialogue(id);
			if (dialogue.error) {
				showAlert(dialogue.error, 'danger');
				return;
			}

			setSelectedDialogue(dialogue);
			setDialogueId(dialogue.id);
			setModerationProfile(dialogue.moderation_profile_id);
			setTopic(dialogue.topic);
			setDescription(dialogue.description);
			setStartTime(utcToLocalDatetimeInput(dialogue.start_time));
			setEndTime(utcToLocalDatetimeInput(dialogue.expiry_time));
			setNumGroups(dialogue.groups);
			setNumStudents(dialogue.participants);

			// Set output type
			if (dialogue.summary && dialogue.transcript) {
				setOutputType('both');
			} else if (dialogue.summary) {
				setOutputType('summary');
			} else {
				setOutputType('transcript');
			}

			// Set dialogue type
			if (dialogue.merged && dialogue.separate) {
				setDialogueType('both');
			} else if (dialogue.merged) {
				setDialogueType('merged');
			} else {
				setDialogueType('separate');
			}

			setAssociatedDocuments(dialogue.associated_documents || []);

			// Disable form if dialogue has started
			const startDate = new Date(dialogue.start_time);
			if (startDate < new Date()) {
				setFormDisabled(true);
			} else {
				setFormDisabled(false);
			}

			setModalOpen(true);
		} catch (error) {
			console.error('Error fetching dialogue details:', error);
			showAlert('Failed to load dialogue details', 'danger');
		}
	};

	const deleteDialogue = async (id) => {
		if (!window.confirm('Are you sure you want to delete this dialogue?')) {
			return;
		}

		try {
			const response = await dialoguesAPI.deleteDialogue(id);
			if (!response.error) {
				showAlert(
					response.message || 'Dialogue deleted successfully',
					'success'
				);
				fetchDialogues();
			} else {
				showAlert(response.error, 'danger');
			}
		} catch (error) {
			console.error('Error deleting dialogue:', error);
			showAlert('Failed to delete dialogue', 'danger');
		}
	};

	const endDialogue = async (id) => {
		try {
			const response = await dialoguesAPI.endDialogue(id);
			if (!response.error) {
				showAlert(response.message || 'Dialogue ended successfully', 'success');
				fetchDialogues();
			} else {
				showAlert(response.error, 'danger');
			}
		} catch (error) {
			console.error('Error ending dialogue:', error);
			showAlert('Failed to end dialogue', 'danger');
		}
	};

	const requestDialogueOutput = async (id) => {
		try {
			const response = await dialoguesAPI.requestDialogueOutput(id);
			if (!response.error) {
				showAlert(
					response.message || 'Output request sent successfully',
					'success'
				);
			} else {
				showAlert(response.error, 'danger');
			}
		} catch (error) {
			console.error('Error requesting dialogue output:', error);
			showAlert('Failed to request output', 'danger');
		}
	};

	const requestGroupOutput = async (id) => {
		try {
			const response = await dialoguesAPI.requestGroupOutput(id);
			if (!response.error) {
				showAlert(
					response.message || 'Group output request sent successfully',
					'success'
				);
			} else {
				showAlert(response.error, 'danger');
			}
		} catch (error) {
			console.error('Error requesting group output:', error);
			showAlert('Failed to request group output', 'danger');
		}
	};

	const viewGroups = async (dialogueId) => {
		try {
			const response = await dialoguesAPI.getDialogueGroups(dialogueId);
			if (!response.error) {
				setGroups(response);
				setSelectedDialogue(dialogues.find((d) => d.id === dialogueId));
				setGroupModalOpen(true);
			} else {
				showAlert(response.error, 'danger');
			}
		} catch (error) {
			console.error('Error fetching dialogue groups:', error);
			showAlert('Failed to load dialogue groups', 'danger');
		}
	};

	const resetForm = () => {
		setDialogueId('');
		setModerationProfile('');
		setTopic('');
		setDescription('');
		setStartTime('');
		setEndTime('');
		setNumGroups(1);
		setNumStudents(1);
		setOutputType('both');
		setDialogueType('both');
		setDocuments(null);
		setAssociatedDocuments([]);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		const now = new Date();
		const startDate = new Date(startTime);
		const endDate = new Date(endTime);

		if (startDate < now) {
			showAlert('Start time cannot be in the past', 'danger');
			return;
		}

		if (endDate <= startDate) {
			showAlert('End time must be after start time', 'danger');
			return;
		}

		try {
			const formData = new FormData();

			formData.append('moderation_profile_id', moderationProfile);
			formData.append('topic', topic);
			formData.append('description', description);
			formData.append('start_time', localToUTC(startTime));
			formData.append('end_time', localToUTC(endTime));
			formData.append('groups', numGroups);
			formData.append('students', numStudents);

			// Set output type booleans
			formData.append(
				'summary',
				outputType === 'summary' || outputType === 'both'
			);
			formData.append(
				'transcript',
				outputType === 'transcript' || outputType === 'both'
			);

			// Set dialogue type booleans
			formData.append(
				'separate',
				dialogueType === 'separate' || dialogueType === 'both'
			);
			formData.append(
				'merged',
				dialogueType === 'merged' || dialogueType === 'both'
			);

			if (dialogueId) {
				formData.append('dialogueId', dialogueId);
			}

			if (documents) {
				for (let i = 0; i < documents.length; i++) {
					formData.append('documents', documents[i]);
				}
			}

			let response;
			if (dialogueId) {
				response = await dialoguesAPI.updateDialogue(dialogueId, formData);
			} else {
				response = await dialoguesAPI.createDialogue(formData);
			}

			if (!response.error) {
				showAlert(response.message || 'Dialogue saved successfully', 'success');
				setModalOpen(false);
				fetchDialogues();
			} else {
				showAlert(response.error, 'danger');
			}
		} catch (error) {
			console.error('Error saving dialogue:', error);
			showAlert('Failed to save dialogue', 'danger');
		}
	};

	const handleFileChange = (e) => {
		const files = e.target.files;

		// Validate file extensions
		for (let i = 0; i < files.length; i++) {
			const extension = files[i].name.split('.').pop().toLowerCase();
			if (
				!['csv', 'doc', 'docx', 'txt', 'pdf', 'xls', 'xlsx'].includes(extension)
			) {
				showAlert(
					'Only CSV, DOC, DOCX, TXT, PDF, XLS, XLSX files are allowed',
					'danger'
				);
				e.target.value = null; // Clear the file input
				return;
			}
		}

		setDocuments(files);
	};

	const handleDeleteDocument = async (filename) => {
		if (!dialogueId) return;

		try {
			const response = await dialoguesAPI.deleteDialogueFile(
				dialogueId,
				filename
			);

			if (!response.error) {
				showAlert(response.message || 'File deleted successfully', 'success');
				setAssociatedDocuments(
					associatedDocuments.filter((doc) => doc !== filename)
				);

				// Refresh dialogue details
				editDialogue(dialogueId);
			} else {
				showAlert(response.error, 'danger');
			}
		} catch (error) {
			console.error('Error deleting file:', error);
			showAlert('Failed to delete file', 'danger');
		}
	};

	const filteredDialogues = dialogues.filter((dialogue) => {
		const startDate = new Date(dialogue.start_time).getTime();
		const endDate = new Date(dialogue.expiry_time).getTime();
		const now = Date.now();

		switch (filter) {
			case '1': // Not Started
				return startDate > now;
			case '2': // Active
				return startDate <= now && endDate >= now;
			case '3': // Expired
				return endDate < now;
			default: // All
				return true;
		}
	});

	return (
		<div>
			<Navbar />
			<Alert />

			<div id="dialogueList" className="col-md-8 offset-md-2">
				<div className="card mt-4">
					<div className="card-body">
						Go to the following URL to connect to a dialogue instance:{' '}
						<a
							href={`${baseUrl}chat`}
						>{`${window.location.origin}${baseUrl}chat`}</a>
					</div>
				</div>

				<h2 className="my-4">Dialogue Instances</h2>

				<div className="my-4 d-flex justify-content-between">
					<button
						type="button"
						className="btn btn-primary"
						onClick={createDialogue}
					>
						Create New Dialogue Instance
					</button>

					<div className="col-auto">
						<select
							className="form-control"
							id="dialogueFilter"
							value={filter}
							onChange={handleFilterChange}
						>
							<option value="0">All</option>
							<option value="1">Not Started</option>
							<option value="2">Active</option>
							<option value="3">Expired</option>
						</select>
					</div>
				</div>

				<table className="table table-bordered">
					<thead className="thead-dark">
						<tr>
							<th>ID</th>
							<th>Topic</th>
							<th>Start Time</th>
							<th>Expiry Time</th>
							<th>Status</th>
							<th>Actions</th>
						</tr>
					</thead>
					<tbody>
						{filteredDialogues.map((dialogue) => {
							const { status, className } = getDialogueStatus(
								dialogue.start_time,
								dialogue.expiry_time
							);

							return (
								<tr key={dialogue.id}>
									<td>{dialogue.id}</td>
									<td className="text-break">{dialogue.title}</td>
									<td>{formatDate(dialogue.start_time)}</td>
									<td>{formatDate(dialogue.expiry_time)}</td>
									<td>
										<span className={`badge ${className}`}>{status}</span>
									</td>
									<td>
										<div className="dropdown">
											<button
												className="btn btn-sm btn-outline-secondary dropdown-toggle"
												type="button"
												id={`dropdownMenuButton${dialogue.id}`}
												data-toggle="dropdown"
												aria-haspopup="true"
												aria-expanded="false"
											>
												Actions
											</button>
											<div
												className="dropdown-menu"
												aria-labelledby={`dropdownMenuButton${dialogue.id}`}
											>
												<button
													className="dropdown-item"
													onClick={() => viewGroups(dialogue.id)}
												>
													<i className="fas fa-users mr-2"></i>
													View Groups
												</button>

												<button
													className="dropdown-item"
													onClick={() => editDialogue(dialogue.id)}
												>
													<i className="fas fa-edit mr-2"></i>
													Edit
												</button>

												{status === 'Active' && (
													<button
														className="dropdown-item"
														onClick={() => endDialogue(dialogue.id)}
													>
														<i className="fas fa-stop mr-2"></i>
														End
													</button>
												)}

												{(status === 'Active' || status === 'Expired') && (
													<button
														className="dropdown-item"
														onClick={() => requestDialogueOutput(dialogue.id)}
													>
														<i className="fas fa-download mr-2"></i>
														Request Output
													</button>
												)}

												<div className="dropdown-divider"></div>

												<button
													className="dropdown-item text-danger"
													onClick={() => deleteDialogue(dialogue.id)}
												>
													<i className="fas fa-trash mr-2"></i>
													Delete
												</button>
											</div>
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			{/* Create/Edit Dialogue Modal */}
			<Modal
				id="createDialogueModal"
				title={
					selectedDialogue ? 'Edit Dialogue Instance' : 'New Dialogue Instance'
				}
				isOpen={modalOpen}
				onClose={() => setModalOpen(false)}
			>
				<form id="createDialogueForm" onSubmit={handleSubmit}>
					<input
						type="hidden"
						id="dialogueId"
						name="dialogueId"
						value={dialogueId}
					/>

					<div className="form-group">
						<label htmlFor="moderationProfile">Moderation Profile</label>
						<select
							className="form-control"
							id="moderationProfile"
							name="moderationProfile"
							value={moderationProfile}
							onChange={(e) => setModerationProfile(e.target.value)}
							disabled={formDisabled}
							required
						>
							<option value="">-- Select a moderation profile --</option>
							{moderationProfiles.map((profile) => (
								<option key={profile.id} value={profile.id}>
									{profile.title}
								</option>
							))}
						</select>
					</div>

					<div className="form-group">
						<label htmlFor="startTime">Start Time</label>
						<input
							type="datetime-local"
							step="any"
							className="form-control"
							id="startTime"
							name="startTime"
							value={startTime}
							onChange={(e) => setStartTime(e.target.value)}
							min="2024-01-01T00:00"
							max="9999-12-31T00:00"
							disabled={formDisabled}
							required
						/>
					</div>

					<div className="form-group">
						<label htmlFor="endTime">End Time</label>
						<input
							type="datetime-local"
							step="any"
							className="form-control"
							id="endTime"
							name="endTime"
							value={endTime}
							onChange={(e) => setEndTime(e.target.value)}
							min="2024-01-01T00:00"
							max="9999-12-31T00:00"
							disabled={formDisabled}
							required
						/>
					</div>

					<div className="form-group">
						<label htmlFor="topic">Topic</label>
						<input
							type="text"
							className="form-control"
							id="topic"
							name="topic"
							value={topic}
							onChange={(e) => setTopic(e.target.value)}
							minLength="1"
							maxLength="255"
							required
						/>
					</div>

					<div className="form-group">
						<label htmlFor="description">Description</label>
						<textarea
							className="form-control"
							id="description"
							name="description"
							rows="4"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							minLength="1"
							required
						></textarea>
					</div>

					<div className="form-group">
						<label htmlFor="documents">Upload Files (Optional)</label>
						<input
							type="file"
							className="form-control-file"
							id="documents"
							name="documents"
							onChange={handleFileChange}
							multiple
						/>
						<div id="uploaded-files">
							{associatedDocuments.map((filename, idx) => (
								<div key={idx}>
									{filename}
									<button
										className="btn btn-sm btn-link"
										type="button"
										onClick={() => handleDeleteDocument(filename)}
									>
										Remove
									</button>
								</div>
							))}
						</div>
					</div>

					<div className="form-group">
						<label htmlFor="students">Number of Students</label>
						<input
							type="number"
							className="form-control"
							id="students"
							name="students"
							value={numStudents}
							onChange={(e) => setNumStudents(parseInt(e.target.value))}
							min="1"
							disabled={formDisabled}
							required
						/>
					</div>

					<div className="form-group">
						<label htmlFor="groups">Number of Groups</label>
						<input
							type="number"
							className="form-control"
							id="groups"
							name="groups"
							value={numGroups}
							onChange={(e) => setNumGroups(parseInt(e.target.value))}
							min="1"
							disabled={formDisabled}
							required
						/>
					</div>

					<div className="form-group">
						<label htmlFor="outputType">Output Type</label>
						<select
							className="form-control"
							id="outputType"
							name="outputType"
							value={outputType}
							onChange={(e) => setOutputType(e.target.value)}
							required
						>
							<option value="summary">Summary</option>
							<option value="transcript">Transcript</option>
							<option value="both">Transcript and Summary</option>
						</select>
					</div>

					<div className="form-group">
						<label htmlFor="dialogueType">Dialogue Type</label>
						<select
							className="form-control"
							id="dialogueType"
							name="dialogueType"
							value={dialogueType}
							onChange={(e) => setDialogueType(e.target.value)}
							required
						>
							<option value="separate">Separate</option>
							<option value="merged">Merged</option>
							<option value="both">Separate and Merged</option>
						</select>
					</div>

					<div className="form-group text-right">
						<button type="submit" className="btn btn-primary">
							{selectedDialogue ? 'Update' : 'Create'}
						</button>
					</div>
				</form>
			</Modal>

			{/* View Groups Modal */}
			<Modal
				id="groupModal"
				title="View Groups"
				isOpen={groupModalOpen}
				onClose={() => setGroupModalOpen(false)}
			>
				<div className="accordion" id="groupAccordion">
					{groups.map((group) => (
						<div className="card" key={group.id}>
							<div className="card-header" id={`heading${group.id}`}>
								<h2 className="mb-0">
									<button
										className="btn btn-link"
										type="button"
										data-toggle="collapse"
										data-target={`#collapse${group.id}`}
										aria-expanded="true"
										aria-controls={`collapse${group.id}`}
									>
										Group {group.id}
										{selectedDialogue &&
											selectedDialogue.start_time <
												new Date().toISOString() && (
												<button
													className="btn btn-sm btn-outline-primary request-output"
													onClick={() => requestGroupOutput(group.id)}
												>
													Request Output
												</button>
											)}
									</button>
								</h2>
							</div>

							<div
								id={`collapse${group.id}`}
								className="collapse show"
								aria-labelledby={`heading${group.id}`}
							>
								<div className="card-body">
									<ul className="list-group list-group-flush">
										{group.participants.map((participant, idx) => (
											<li className="list-group-item" key={idx}>
												{participant}
											</li>
										))}
									</ul>
								</div>
							</div>
						</div>
					))}
				</div>
			</Modal>
		</div>
	);
};

export default DialoguesPage;
